import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import {
  forwardRef,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
  useCallback,
} from 'react';

import { RippleRoot } from '../../RippleRoot';
import { cn } from '../../utils';
import {
  MultiselectItemContextProvider,
  useMultiselectContext,
} from '../MultiselectInput.context';

const multiselectItemVariants = cva('relative', {
  variants: {
    variant: {
      default:
        'flex flex-row gap-2 px-3 py-2 items-center hover:bg-primary-5 cursor-pointer data-[selected=true]:bg-primary-10',
      none: '',
    },
  },
});

type MultiselectItemVariantProps = VariantProps<typeof multiselectItemVariants>;

interface MultiselectItemProps
  extends MultiselectItemVariantProps,
    Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  optionId: string;
  asChild?: boolean;
  children?: ReactNode | ((selected: boolean) => ReactNode);
  disabled?: boolean;
}

export const MultiselectItem = forwardRef<HTMLDivElement, MultiselectItemProps>(
  (
    {
      className,
      asChild,
      optionId,
      onClick,
      children,
      variant = 'none',
      disabled,
      ...props
    },
    ref,
  ) => {
    const { selectedSet, toggleOption } = useMultiselectContext();

    const Comp = asChild ? Slot : 'div';
    const selected = selectedSet.has(optionId);

    const handleToggleOption = useCallback(
      (e: MouseEvent<HTMLDivElement>) => {
        if (disabled) {
          return;
        }

        toggleOption(optionId);
        onClick?.(e);
      },
      [optionId, toggleOption, onClick],
    );

    return (
      <MultiselectItemContextProvider selected={selected}>
        <Comp
          ref={ref}
          className={cn(
            multiselectItemVariants({ variant }),
            className,
            disabled && 'opacity-50 hover:bg-inherit cursor-auto',
          )}
          onClick={handleToggleOption}
          data-selected={selected}
          data-disabled={disabled}
          {...props}
        >
          {typeof children === 'function' ? children(selected) : children}
          {variant === 'default' && !disabled && (
            <RippleRoot
              className="bg-primary-60"
              pulseDuration={0.7}
              pulseOpacity={0.6}
            />
          )}
        </Comp>
      </MultiselectItemContextProvider>
    );
  },
);

MultiselectItem.displayName = 'MultiselectItem';
